import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { imageApi } from '../../../api/imageApi.js';
import { useImageGestures } from './useImageGestures.js';
import './imageModal.css';
import './zoomableImage.css';
import './randomScreen.css';

const revokeBlobUrl = (url) => {
    if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
    }
};

const RandomScreen = () => {
    const navigate = useNavigate();
    const [imageSrc, setImageSrc] = useState('');
    const [previousImage, setPreviousImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const imageSrcRef = useRef('');
    const previousImageRef = useRef(null);

    const loadRandomImage = useCallback(async () => {
        setLoading(true);
        try {
            const imageUrl = await imageApi.getRandomImageBytes();
            revokeBlobUrl(imageSrcRef.current);
            imageSrcRef.current = imageUrl;
            setImageSrc(imageUrl);
        } catch (error) {
            console.error('Error fetching the image:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRandomImage();
        return () => {
            revokeBlobUrl(imageSrcRef.current);
            revokeBlobUrl(previousImageRef.current);
        };
    }, [loadRandomImage]);

    const handleNext = useCallback(async () => {
        if (loading) return;

        setLoading(true);
        try {
            const imageUrl = await imageApi.getRandomImageBytes();
            revokeBlobUrl(previousImageRef.current);
            previousImageRef.current = imageSrcRef.current;
            setPreviousImage(imageSrcRef.current);

            imageSrcRef.current = imageUrl;
            setImageSrc(imageUrl);
        } catch (error) {
            console.error('Error fetching the image:', error);
        } finally {
            setLoading(false);
        }
    }, [loading]);

    const handleBack = useCallback(() => {
        if (!previousImageRef.current || loading) return;

        const restored = previousImageRef.current;
        revokeBlobUrl(imageSrcRef.current);
        imageSrcRef.current = restored;
        previousImageRef.current = null;

        setImageSrc(restored);
        setPreviousImage(null);
    }, [loading]);

    const { containerRef, transform, resetZoom, isZoomed } = useImageGestures({
        resetKey: imageSrc,
    });

    useLayoutEffect(() => {
        resetZoom();
    }, [imageSrc, resetZoom]);

    const canGoBack = Boolean(previousImage) && !loading;

    return (
        <div
            className={`random-screen fullscreen-image-container image-gesture-container${isZoomed ? ' is-zoomed' : ''}`}
            ref={containerRef}
        >
            <div className={`image-zoom-viewport${loading ? ' random-screen-loading' : ''}`}>
                {imageSrc && (
                    <img
                        src={imageSrc}
                        alt="Random"
                        draggable={false}
                        style={{
                            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                        }}
                        onLoad={resetZoom}
                    />
                )}
            </div>

            {isZoomed && (
                <button
                    type="button"
                    className="reset-zoom-button"
                    onClick={resetZoom}
                    title="Reset zoom"
                >
                    Reset zoom
                </button>
            )}

            <button
                type="button"
                className="close-button"
                onClick={() => navigate('/other')}
                title="Close"
            >
                X
            </button>

            <div className="random-screen-controls">
                <button
                    type="button"
                    className="random-screen-nav"
                    onClick={handleBack}
                    disabled={!canGoBack}
                    aria-label="Previous image"
                >
                    Back
                </button>
                <button
                    type="button"
                    className="random-screen-nav"
                    onClick={handleNext}
                    disabled={loading}
                    aria-label="Next random image"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export { RandomScreen };
